import { memo } from 'react';
import { motion } from 'motion/react';
import { twJoin } from 'tailwind-merge';

import { SimplifiedCourseProgress } from '@/models/StudentCourseProgress';
import ImageUpload from '@/components/ui/custom/input/ImageUpload';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import SingleFilter from '@/components/filter/SingleFilter';
import { subheading2 } from '@/styles/styles';

type Props = {
  courseProgress: SimplifiedCourseProgress;
  onSelect: (courseProgressId: number) => void;
  collection: SimplifiedCourseProgress[];
  banner?: boolean;
  progress?: boolean;
};

const CourseProgressDisplay = memo((props: Props) => {
  const { courseProgress, onSelect, collection, banner, progress } = props;

  return (
    <section className="flex gap-2.5 w-full">
      <ConditionalRenderer
        condition={courseProgress.coursePath.course.bannerImg && banner}
      >
        <ImageUpload
          readonly
          value={courseProgress.coursePath.course.bannerImg}
          className={{ div: 'h-[4rem]', base: 'w-fit' }}
        />
      </ConditionalRenderer>

      <div className="flex flex-col gap-6 grow justify-center">
        <ConditionalRenderer
          condition={collection && collection.length > 1}
          fallback={
            <h2 className={twJoin(subheading2, 'text-base-content font-600')}>
              {courseProgress.coursePath.course.name}
            </h2>
          }
        >
          <SingleFilter
            className={{
              div: 'gap-2.5',
              reference: 'gap-2.5',
              text: twJoin(subheading2, 'text-base-content font-600'),
              icon: 'w-5 text-base-content',
            }}
            label={courseProgress.coursePath.course.name}
            name="id"
            type="single"
            mode="static"
            value={courseProgress.id}
            setValue={(_, value: number) => onSelect(value)}
            options={collection.map(option => ({
              label: option.coursePath.course.name,
              value: option.id,
            }))}
          />
        </ConditionalRenderer>

        <ConditionalRenderer condition={progress}>
          <div
            data-testid="progress-container"
            className="w-full rounded-full h-2.5 bg-secondary/40"
          >
            <motion.div
              className={twJoin(
                'bg-accent h-full rounded-full bg-linear-to-r from-primary/80 to-secondary/80',
                courseProgress.progress === 1
                  ? 'rounded-br-full'
                  : 'rounded-r-full',
              )}
              animate={{
                width: `${(courseProgress.progress ?? 0) * 100}%`,
              }}
            />
          </div>
        </ConditionalRenderer>
      </div>
    </section>
  );
});

export default CourseProgressDisplay;
